import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Pagination from "@/components/Pagination";
import { ShieldWarning, Receipt, ArrowSquareOut, MagnifyingGlass, Calendar, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function AdminReview() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, page_size: pageSize };
      if (search) params.search = search;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/invoices/pending-admin-review", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
      setSelected(new Set());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    if (selected.size === rows.length && rows.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((r) => r.id)));
    }
  };
  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(
      `Delete ${ids.length} invoice${ids.length === 1 ? "" : "s"}? Underlying files will be removed and each parent PO's fulfilment recomputed. This is permanent.`
    )) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.delete("/invoices/bulk", { data: { ids } });
      let msg = `${data.deleted} invoice${data.deleted === 1 ? "" : "s"} deleted`;
      if (data.storage_removed_count) msg += ` · ${data.storage_removed_count} file${data.storage_removed_count === 1 ? "" : "s"} cleaned up`;
      toast.success(msg);
      if (data.failed?.length) toast.warning(`${data.failed.length} could not be deleted`);
      await load(1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Load on mount + when date range changes
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (user && user.role !== "admin") {
    return (
      <div className="p-10 text-center">
        <ShieldWarning size={48} className="mx-auto text-neutral-400 mb-3" />
        <h2 className="font-display text-2xl font-bold mb-2">Admin Only</h2>
        <p className="text-sm text-neutral-500">This page lists invoices auto-rejected by AI that require admin review.</p>
      </div>
    );
  }

  return (
    <div className="p-8 sm:p-10 max-w-[1400px] mx-auto" data-testid="admin-review-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">Compliance Queue</div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <ShieldWarning size={32} className="text-[#B06000]" weight="fill" />
            Admin Review
          </h1>
          <p className="text-sm text-neutral-500 mt-2">{total} invoice{total === 1 ? "" : "s"} auto-rejected by AI, awaiting your manual decision.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="PO#, vendor, invoice#, uploader…"
              className="rounded-sm pl-9 h-10"
              data-testid="admin-review-search"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
            <Calendar size={11} /> From
          </label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            max={toDate || undefined}
            className="rounded-sm h-10 tabular-nums w-[160px]"
            data-testid="admin-review-from-date"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
            <Calendar size={11} /> To
          </label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            min={fromDate || undefined}
            className="rounded-sm h-10 tabular-nums w-[160px]"
            data-testid="admin-review-to-date"
          />
        </div>
        {(search || fromDate || toDate) && (
          <Button
            variant="ghost"
            className="rounded-sm h-10 text-neutral-500 hover:text-neutral-900"
            onClick={() => { setSearch(""); setFromDate(""); setToDate(""); }}
            data-testid="admin-review-clear-filters"
          >
            Clear
          </Button>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        {selected.size > 0 && (
          <div className="flex items-center justify-between px-6 py-3 bg-[#FCE8E6] border-b border-[#F3B2AC] text-sm text-[#C5221F]" data-testid="admin-review-bulk-toolbar">
            <span className="font-medium">
              {selected.size} invoice{selected.size === 1 ? "" : "s"} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-sm h-8 text-[#C5221F] hover:text-[#C5221F]"
                onClick={() => setSelected(new Set())}
                data-testid="admin-review-bulk-clear"
              >
                Clear
              </Button>
              <Button
                size="sm"
                className="rounded-sm h-8 bg-[#C5221F] hover:bg-[#A80D0B] text-white gap-1.5"
                disabled={bulkDeleting}
                onClick={bulkDelete}
                data-testid="admin-review-bulk-delete-btn"
              >
                <Trash size={14} weight="bold" />
                {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
              </Button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="w-10 px-3 py-3 text-center">
                  <Checkbox
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAllOnPage}
                    aria-label="Select all invoices on this page"
                    data-testid="admin-review-select-all"
                  />
                </th>
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">Invoice #</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">PO</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Vendor</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">AI Reason</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Uploader</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Uploaded</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-16">
                    <Receipt size={36} className="mx-auto text-neutral-300 mb-3" />
                    <div className="text-sm text-neutral-500">No invoices pending admin review</div>
                    <div className="text-xs text-neutral-400 mt-1">
                      {search || fromDate || toDate ? "Try clearing filters." : "All auto-rejected invoices have been resolved."}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((inv, idx) => {
                  const reason = inv.validation?.mismatches?.[0]?.reason || "Auto-rejected by AI";
                  const checked = selected.has(inv.id);
                  return (
                    <tr key={inv.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${idx % 2 ? "bg-neutral-50/40" : ""} ${checked ? "bg-red-50/50" : ""}`} data-testid={`admin-review-row-${inv.id}`}>
                      <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleOne(inv.id)}
                          aria-label={`Select invoice ${inv.extracted_data?.invoice_number || inv.original_filename}`}
                          data-testid={`admin-review-select-${inv.id}`}
                        />
                      </td>
                      <td className="px-6 py-3 font-mono-num text-sm text-neutral-900">
                        {inv.extracted_data?.invoice_number || inv.original_filename}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/pos/${inv.po_id}`} className="font-mono-num text-primary hover:underline">{inv.po_number}</Link>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">{inv.extracted_data?.vendor_name || "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmt(inv.extracted_data?.total_amount)}</td>
                      <td className="px-4 py-3 text-[#C5221F] text-xs">{reason}</td>
                      <td className="px-4 py-3 text-neutral-600 text-xs">{inv.uploaded_by_name || "—"}</td>
                      <td className="px-4 py-3 text-neutral-500 tabular-nums whitespace-nowrap">
                        {new Date(inv.uploaded_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/pos/${inv.po_id}`} className="inline-flex items-center gap-1 text-primary text-xs uppercase tracking-wider hover:underline" data-testid={`review-link-${inv.id}`}>
                          Review <ArrowSquareOut size={11} />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={(n) => load(n)}
          testIdPrefix="admin-review"
        />
      </div>
    </div>
  );
}
